import ReactPixel from 'react-facebook-pixel'

import { hasCookieConsent } from '../hooks/useCookieConsent/useCookieConsent'
import type { BasketItemWithPrices } from '../hooks/useTotals/useBasketTotals'
import type { CheckoutBasketTotalsQuery } from '../hooks/useTotals/queries/__generated__/checkoutBasketTotals.graphql-interface'

import { AnalyticsEvent, AnalyticsEventName, EVENT_ROUTING } from './events'
import { trackGa4Event } from './gtag'
import { buildContextProperties, buildSuperProperties } from './superProperties'
import { AnalyticsGroups, AnalyticsItem, AnalyticsPayload } from './types'
import { getUtmParams } from './utm'

/**
 * Central tracking service.
 *
 * Emits a single canonical event to every destination — GA4 (via gtag.js), the
 * Meta pixel, and PostHog — translating the canonical payload into each
 * destination's required shape. Per-event routing (see EVENT_ROUTING) decides
 * which destinations receive each event, so join keys and event names stay
 * identical across web, app and terminal.
 */

/** PostHog capture signature, injected by the PostHog module once initialised. */
export type PostHogCapture = (
  event: string,
  properties: Record<string, unknown>,
  groups?: AnalyticsGroups
) => void

let posthogCapture: PostHogCapture | null = null

/** Registers the PostHog capture fn. Called once PostHog is initialised. */
export const registerPostHogCapture = (
  capture: PostHogCapture | null
): void => {
  posthogCapture = capture
}

/** Converts a backend minor-unit amount (e.g. pence) to major units (e.g. £). */
export const toMajorUnits = (minor?: number | null): number | undefined =>
  typeof minor === 'number' ? minor / 100 : undefined

/**
 * Builds the ecommerce `items` array from priced basket lines.
 *
 * The persisted basket only stores ids and quantities, so prices have to come
 * from the totals query — without them GA4 reports £0.00 item revenue.
 * `totalPriceForOneItem` includes the selected options, matching the unit price
 * shown to the customer.
 */
export const toAnalyticsItems = (
  basketItemsWithPrices: BasketItemWithPrices[]
): AnalyticsItem[] =>
  basketItemsWithPrices.map(
    ({ basketItem, outletMenuItem, totalPriceForOneItem }) => ({
      itemId: basketItem.menuItemId,
      itemName: outletMenuItem.name,
      quantity: basketItem.quantity,
      price: toMajorUnits(totalPriceForOneItem),
    })
  )

type AppliedDiscount = NonNullable<
  CheckoutBasketTotalsQuery['checkoutBasketTotals']
>['appliedDiscount']

/**
 * Resolves the GA4 `coupon` parameter. Voucher codes are the customer-facing
 * identifier; automatic offers have no code, so fall back to the offer name.
 */
export const toCouponParam = (
  appliedDiscount: AppliedDiscount | undefined
): string | undefined =>
  appliedDiscount?.discount?.voucher || appliedDiscount?.discount?.name

/** Maps canonical items to the GA4 ecommerce `items` array. */
const toGa4Items = (items: AnalyticsItem[] = []) =>
  items.map(
    ({ itemId, itemName, quantity, price, itemCategory, itemVariant }) => ({
      item_id: itemId,
      item_name: itemName,
      quantity,
      price,
      item_category: itemCategory,
      item_variant: itemVariant,
    })
  )

/**
 * Maps the canonical payload to Meta `content_*` fields.
 *
 * Returns nothing when there is neither a product line nor an outlet to
 * describe — Events Manager flags a `content_type` sent without matching
 * content ids as a broken pixel setup.
 */
const toMetaContents = (payload: AnalyticsPayload) => {
  const { items = [], contentType, outletId } = payload

  if (items.length) {
    return {
      content_type: contentType ?? 'product',
      content_ids: items.map(item => item.itemId),
      contents: items.map(item => ({
        id: item.itemId,
        quantity: item.quantity,
        item_price: item.price,
      })),
      num_items: items.reduce((total, item) => total + (item.quantity ?? 1), 0),
    }
  }

  // Outlet-level ViewContent has no product lines, so the outlet is the content.
  if (contentType && outletId) {
    return { content_type: contentType, content_ids: [outletId] }
  }

  return {}
}

/** Shared GA4/PostHog scalar params derived from the canonical payload. */
const toEventParams = (payload: AnalyticsPayload) => ({
  currency: payload.currency,
  value: payload.value,
  transaction_id: payload.transactionId,
  coupon: payload.coupon,
  method: payload.method,
  item_list_name: payload.itemListName,
  shipping_tier: payload.shippingTier,
  payment_type: payload.paymentType,
  promotion_name: payload.promotionName,
  virtual_currency_name: payload.virtualCurrencyName,
  reason: payload.reason,
  affiliation: payload.affiliation,
  search_term: payload.searchTerm,
})

/** Flattens the canonical payload for PostHog properties (join keys as props). */
const toPostHogProps = (
  payload: AnalyticsPayload,
  eventParams: ReturnType<typeof toEventParams>
) => ({
  ...eventParams,
  order_id: payload.orderId ?? payload.transactionId,
  outlet_id: payload.outletId,
  marketplace_id: payload.marketplaceId,
  items: payload.items,
})

/** Derives the PostHog groups from a payload's join keys, dropping empties.
 * Outlet/order are sent as properties, not groups. */
const deriveGroups = (
  payload: AnalyticsPayload,
  explicit?: AnalyticsGroups
): AnalyticsGroups | undefined => {
  const groups: AnalyticsGroups = {
    marketplace: payload.marketplaceId,
    ...explicit,
  }
  const entries = Object.entries(groups).filter(([, value]) => Boolean(value))
  return entries.length ? Object.fromEntries(entries) : undefined
}

/**
 * Tracks a canonical event across its routed destinations.
 *
 * Each destination owns its own consent behaviour: GA4 falls back to cookieless
 * pings via Consent Mode v2 and PostHog to a server-hashed identity via
 * cookieless mode, so both are always called. Meta has no such fallback, so it
 * is the only one gated on explicit acceptance.
 */
export const track = (
  event: AnalyticsEventName,
  payload: AnalyticsPayload = {},
  groups?: AnalyticsGroups
): void => {
  const routing = EVENT_ROUTING[event]

  const superProps = buildSuperProperties()
  const context = buildContextProperties()
  const utm = getUtmParams()
  const eventParams = toEventParams(payload)

  // GA4 via gtag.js — flat ecommerce params (Consent Mode v2 governs whether
  // anything is actually stored, so it's safe to send regardless of choice).
  // UTM is omitted deliberately: GA4 derives session-scoped campaign attribution
  // from the landing URL, so custom utm_ params never reach the standard reports.
  if (routing.ga4) {
    trackGa4Event(event, {
      ...superProps,
      ...context,
      ...eventParams,
      items: toGa4Items(payload.items),
    })
  }

  // A destination flagged requiresContentType only fires for the pages that set
  // one, so `view_item_list` reaches Meta from the outlet menu but not the list.
  const metaDestination =
    routing.meta?.requiresContentType && !payload.contentType
      ? undefined
      : routing.meta

  if (metaDestination && hasCookieConsent()) {
    const metaParams = {
      value: payload.value,
      currency: payload.currency,
      content_name: payload.affiliation ?? payload.method,
      search_string: payload.searchTerm,
      // Meta types CompleteRegistration's status as a boolean, not a value.
      ...(event === AnalyticsEvent.SignUp && { status: true }),
      ...toMetaContents(payload),
    }
    if (metaDestination.custom)
      ReactPixel.trackCustom(metaDestination.name, metaParams)
    else ReactPixel.track(metaDestination.name, metaParams)
  }

  // PostHog product analytics with per-event group association.
  if (routing.posthog) {
    posthogCapture?.(
      event,
      {
        ...superProps,
        ...context,
        ...utm,
        ...toPostHogProps(payload, eventParams),
      },
      deriveGroups(payload, groups)
    )
  }
}

// Convenience wrappers — typed entry points used by call sites.

export const trackSignUp = (method = 'Email') =>
  track(AnalyticsEvent.SignUp, { method })

export const trackLogin = (method = 'Email') =>
  track(AnalyticsEvent.Login, { method })

export const trackSearch = (searchTerm: string) =>
  track(AnalyticsEvent.Search, { searchTerm })

export const trackViewItemList = (
  itemListName: string,
  payload: AnalyticsPayload = {}
) => track(AnalyticsEvent.ViewItemList, { ...payload, itemListName })

export const trackViewItem = (payload: AnalyticsPayload) =>
  track(AnalyticsEvent.ViewItem, payload)

export const trackAddToCart = (payload: AnalyticsPayload) =>
  track(AnalyticsEvent.AddToCart, payload)

export const trackRemoveFromCart = (payload: AnalyticsPayload) =>
  track(AnalyticsEvent.RemoveFromCart, payload)

export const trackViewCart = (payload: AnalyticsPayload) =>
  track(AnalyticsEvent.ViewCart, payload)

export const trackViewPromotion = (payload: AnalyticsPayload) =>
  track(AnalyticsEvent.ViewPromotion, payload)

export const trackSelectPromotion = (payload: AnalyticsPayload) =>
  track(AnalyticsEvent.SelectPromotion, payload)

export const trackBeginCheckout = (payload: AnalyticsPayload) =>
  track(AnalyticsEvent.BeginCheckout, payload)

export const trackAddShippingInfo = (payload: AnalyticsPayload) =>
  track(AnalyticsEvent.AddShippingInfo, payload)

export const trackAddPaymentInfo = (payload: AnalyticsPayload) =>
  track(AnalyticsEvent.AddPaymentInfo, payload)

export const trackPurchase = (
  payload: AnalyticsPayload,
  groups?: AnalyticsGroups
) => track(AnalyticsEvent.Purchase, payload, groups)

export const trackEarnVirtualCurrency = (
  payload: AnalyticsPayload,
  groups?: AnalyticsGroups
) =>
  track(
    AnalyticsEvent.EarnVirtualCurrency,
    { virtualCurrencyName: 'Stamp', value: 1, ...payload },
    groups
  )

export const trackPaymentFailed = (payload: AnalyticsPayload) =>
  track(AnalyticsEvent.PaymentFailed, payload)

export const trackGenerateLead = () => track(AnalyticsEvent.GenerateLead)

export const trackContact = () => track(AnalyticsEvent.Contact)

export const trackCustomizeProduct = (payload: AnalyticsPayload) =>
  track(AnalyticsEvent.CustomizeProduct, payload)
